/**
 * Befunde der Hebel-Karten (P5-Review #17: „das Kapitel heißt Hebel, die Karten zeigen nur Zahlen"). Je Karte EIN
 * Satz, beim Rendern aus den Daten gerechnet: was die Zahlen zeigen und was man testen könnte — keine Ursache. Zu
 * dünne oder fehlende Daten ergeben `{ fehlt: Grund }` statt eines Satzes. Dazu `festeStellen` (#30): eine
 * Nachkommastellenzahl je Spalte statt „0,11 / 0,081 / 0,12" in einer Spalte.
 */
import { formatNumber } from '../../../helpers/numberUtils';

export type Befund = { text: string } | { fehlt: string };

/**
 * Nachkommastellen einer Spalte: zwei geltende Stellen am GRÖSSTEN Wert, für alle Zeilen gleich (0,124 → 2 Stellen:
 * „0,12 / 0,08"; 3,07 → 1 Stelle; ab 10 ganze Zahlen).
 */
export function festeStellen(werte: (number | null | undefined)[]): number {
  const max = Math.max(0, ...werte.filter((v): v is number => typeof v === 'number' && Number.isFinite(v)).map(Math.abs));
  if (max === 0 || max >= 10) return 0;
  return Math.max(0, 1 - Math.floor(Math.log10(max)));
}

const fmt = (v: number, stellen: number) => formatNumber(v, stellen);

/** € je 1.000 (Regel 7 des Persona-Umbaus): ab 10 € ohne Cent, darunter mit einer Stelle. */
export function euroJe1000(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  if (v === 0) return '0 €';
  return `${formatNumber(v, Math.abs(v) >= 10 ? 0 : 1)} €`;
}

/** Euro-Betrag im Satz: ab 1.000 € in T€ mit einer Stelle, darunter ganze Euro. */
export function euroKurz(v: number): string {
  return Math.abs(v) >= 1000 ? `${formatNumber(v / 1000, 1)} T€` : `${formatNumber(v, 0)} €`;
}

// ─── Wellen ─────────────────────────────────────────────────────────────────────────────────

export interface WelleBefundZeile {
  label: string;
  kontakte: number;
  anfos: number;
  anfos_vorlaeufig: number;
  anfos_je_1000: number | null;
  wert_je_1000: number | null;
  abmeldungen_je_anmeldung: number | null;
}

/** Ab diesem |z| (zweiseitig 5 %) gilt der Abstand der schwächsten Welle zum Rest als nicht bloß zufällig. */
const Z_GRENZE = 1.96;

/**
 * Welle mit dem kleinsten Erwarteten Wert je 1.000 Kontakte gegen die übrigen (Persona-Umbau A2: Einheit wie P3), dazu
 * Anfos je 1.000 und die Abmeldungen je Anmeldung als Abwägung. Fehlt einer Welle der Euro-Wert, zählen die Anfos je 1.000.
 * Zufallsprüfung: Zwei-Anteile-z-Test (Anfos ÷ Kontakte) der schwächsten Welle gegen die übrigen zusammen — der Euro-Wert
 * ist Anfos × Wert je Anfo, die Unsicherheit steckt in der Zahl der Anfos.
 */
export function wellenBefund(zeilen: WelleBefundZeile[], webinare: number, minAnfos: number): Befund {
  const z = zeilen.filter((r) => r.anfos_je_1000 !== null && r.kontakte > 0);
  if (webinare < 3) return { fehlt: `Kein Befund: erst ${webinare} gelaufene Webinare mit allen drei Wellen (mindestens 3).` };
  if (z.length < 3) return { fehlt: 'Kein Befund: nicht alle drei Wellen haben Kontakte.' };
  const fest = (r: WelleBefundZeile) => r.anfos - r.anfos_vorlaeufig;
  const duenn = z.filter((r) => fest(r) < minAnfos);
  if (duenn.length > 0) {
    return { fehlt: `Kein Befund: ${duenn.map((r) => r.label).join(', ')} unter ${minAnfos} Anfos — die Quoten hängen am Zufall.` };
  }
  const mitEuro = z.every((r) => r.wert_je_1000 !== null);
  const haupt = (r: WelleBefundZeile) => (mitEuro ? (r.wert_je_1000 as number) : (r.anfos_je_1000 as number));
  const sortiert = [...z].sort((a, b) => haupt(a) - haupt(b));
  const schwach = sortiert[0];
  const rest = sortiert.slice(1);
  const stellenAnfos = festeStellen(z.map((r) => r.anfos_je_1000));
  const spanne = (vs: number[], f: (v: number) => string) => {
    const a = f(Math.min(...vs));
    const b = f(Math.max(...vs));
    return a === b ? a : `${a}–${b}`;
  };
  const anfosText = `Anfos ${fmt(schwach.anfos_je_1000 as number, stellenAnfos)} gegen ${spanne(
    rest.map((r) => r.anfos_je_1000 as number),
    (v) => fmt(v, stellenAnfos),
  )} je 1.000`;
  const kopf = mitEuro
    ? `${schwach.label}: kleinster Erwarteter Wert je 1.000 Kontakte (${euroJe1000(schwach.wert_je_1000)} gegen ${spanne(
        rest.map((r) => r.wert_je_1000 as number),
        (v) => euroJe1000(v).replace(' €', ''),
      )} €; ${anfosText})`
    : `${schwach.label}: wenigste Anfos je 1.000 Kontakte (${fmt(schwach.anfos_je_1000 as number, stellenAnfos)} gegen ${spanne(
        rest.map((r) => r.anfos_je_1000 as number),
        (v) => fmt(v, stellenAnfos),
      )})`;
  // Abmeldungen je Anmeldung: „gleich viele", wenn die schwächste Welle höchstens 10 % vom Schnitt der übrigen abweicht.
  const abmRest = rest.map((r) => r.abmeldungen_je_anmeldung).filter((v): v is number => v !== null);
  const abmSchnitt = abmRest.length ? abmRest.reduce((s, v) => s + v, 0) / abmRest.length : null;
  const abm = schwach.abmeldungen_je_anmeldung;
  const st = (v: number) => fmt(v, 1);
  const abmText =
    abm === null || abmSchnitt === null || abmSchnitt === 0
      ? ''
      : Math.abs(abm - abmSchnitt) / abmSchnitt <= 0.1
        ? ` bei gleich vielen Abmeldungen je Anmeldung (${st(abm)} gegen ${spanne(abmRest, st)})`
        : ` bei ${abm > abmSchnitt ? 'mehr' : 'weniger'} Abmeldungen je Anmeldung (${st(abm)} gegen ${spanne(abmRest, st)})`;
  // z-Test schwächste Welle gegen die übrigen zusammen.
  const n1 = schwach.kontakte;
  const x1 = fest(schwach);
  const n2 = rest.reduce((s, r) => s + r.kontakte, 0);
  const x2 = rest.reduce((s, r) => s + fest(r), 0);
  const p = (x1 + x2) / (n1 + n2);
  const se = Math.sqrt(p * (1 - p) * (1 / n1 + 1 / n2));
  const zWert = se > 0 ? (x1 / n1 - x2 / n2) / se : 0;
  const sicher = Math.abs(zWert) >= Z_GRENZE;
  return {
    text:
      `${kopf}${abmText}` +
      (sicher
        ? ` — Kandidat für einen Test ohne diese Welle.`
        : ` — bei ${formatNumber(x1, 0)} Anfos noch nicht sicher vom Zufall zu trennen; ein Kandidat für einen Test ohne diese Welle, kein Urteil.`),
  };
}

// ─── Nachfass ───────────────────────────────────────────────────────────────────────────────

export interface NachfassBefundEingabe {
  /** Summe der Karte. */
  anfos: number;
  anfos_vorlaeufig: number;
  wert_je_1000: number | null;
  wert_je_anfo: number | null;
  /** Heat-Up-Vergleich (P3 „Webinar-Einladungen"), `null` ohne. */
  heatup_wert_je_1000: number | null;
  webinare_im_fenster: number;
  webinare_mit_nachfass: number;
  ohne_nachfass: { start_date: string }[];
}

const tagKurz = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

/**
 * Schätzung für die Webinare ohne Nachfass (Persona-Umbau A1): Ø Z = Nachfass-Anfos (ohne vorläufige) ÷ Webinare mit
 * Nachfass, A = K × Z, B = A × Ø Erwarteter Wert je Nachfass-Anfo. `null` ohne Webinar mit Nachfass.
 */
export function nachfassOhneSchaetzung(e: NachfassBefundEingabe): { k: number; z: number; anfos: number; wert: number | null } | null {
  if (e.webinare_mit_nachfass <= 0) return null;
  const k = e.ohne_nachfass.length;
  const z = (e.anfos - e.anfos_vorlaeufig) / e.webinare_mit_nachfass;
  const anfos = k * z;
  return { k, z, anfos, wert: e.wert_je_anfo === null ? null : anfos * e.wert_je_anfo };
}

/** Wie lange eine Lücke als „aktuell" gilt: ein Webinar ohne Nachfass in den letzten 4 Wochen ist ein Handlungsauftrag. */
export const NACHFASS_LUECKE_AKTUELL_TAGE = 28;

/** Letztes Webinar ohne Nachfass im Fenster und ob es jünger als 4 Wochen ist (Runde 2, Marketing). */
export function nachfassLuecke(e: Pick<NachfassBefundEingabe, 'ohne_nachfass'>, heute: string): { letzte: string | null; aktuell: boolean } {
  const letzte = e.ohne_nachfass.map((w) => w.start_date.slice(0, 10)).sort().pop() ?? null;
  if (!letzte) return { letzte: null, aktuell: false };
  const tage = (Date.parse(`${heute}T00:00:00Z`) - Date.parse(`${letzte}T00:00:00Z`)) / 86_400_000;
  return { letzte, aktuell: tage <= NACHFASS_LUECKE_AKTUELL_TAGE };
}

/**
 * Befund der Nachfass-Karte (Runde 2, GF + Marketing): KEIN Vergleich mit Heat-Ups — der Nachfass geht an Angemeldete,
 * „1.551 € gegen 18 €" legte eine Umschichtung nahe, die es nicht gibt. Stattdessen, was ein Nachfass je Webinar bringt.
 * Webinare ohne Nachfass: „höchstens" statt „entgangen" (Angemeldete kaufen teils auch ohne Nachfass), und eine alte
 * Lücke (Mai/Juni) steht als erledigt da statt als offener Auftrag.
 */
export function nachfassBefund(e: NachfassBefundEingabe, minAnfos = 20, heute?: string): Befund {
  const fest = e.anfos - e.anfos_vorlaeufig;
  if (fest < minAnfos || e.wert_je_1000 === null) {
    return { fehlt: `Kein Befund: nur ${formatNumber(fest, 0)} Anfos über den Nachfass im Zeitraum (mindestens ${minAnfos}).` };
  }
  const x = euroJe1000(e.wert_je_1000);
  const sch = nachfassOhneSchaetzung(e);
  // Audit C7 (30.10.2026): ganze Sätze mit Subjekt statt Stichworten („Nachfass 1.551 € …").
  const jeWebinar =
    sch && sch.z > 0
      ? `, im Schnitt ${formatNumber(sch.z, 1)} Anfos${e.wert_je_anfo === null ? '' : ` / ≈ ${euroKurz(sch.z * e.wert_je_anfo)}`} je Webinar mit Nachfass`
      : '';
  const kopf = `Der Nachfass an die Angemeldeten bringt ${x} Erwarteten Wert je 1.000 Empfänger${jeWebinar}.`;
  const n = e.ohne_nachfass.length;
  if (n === 0) return { text: `${kopf} Jedes Webinar des Zeitraums hatte einen Nachfass.` };
  const daten = e.ohne_nachfass.map((w) => tagKurz(w.start_date)).join(', ');
  const luecke = heute ? nachfassLuecke(e, heute) : { letzte: null, aktuell: true };
  const hoechstens =
    sch && sch.anfos > 0
      ? ` Ein Nachfass hätte dort höchstens ≈ ${formatNumber(Math.round(sch.anfos), 0)} Anfos${sch.wert === null ? '' : ` / ≈ ${euroKurz(sch.wert)}`} gebracht (Schätzung — Angemeldete kaufen teils auch ohne Nachfass).`
      : '';
  const ohne = `${n} von ${e.webinare_im_fenster} Webinaren ${n === 1 ? 'hatte' : 'hatten'} keinen Nachfass`;
  if (!luecke.aktuell && luecke.letzte) {
    return {
      text: `${kopf} ${ohne}, alle bis ${tagKurz(luecke.letzte)} (${daten}) — seitdem hatte jedes Webinar einen.${hoechstens}`,
    };
  }
  return { text: `${kopf} ${ohne}, zuletzt ${daten}.${hoechstens}` };
}

/** Zeile der Nachfass-Tabelle nach der Gruppierung: Publisher, „übrige Listen" (mit Kindzeilen) oder deren Kinder. */
export interface NachfassGruppe {
  key: string;
  label: string;
  /** Kürzel unter dem Namen (bekannte Publisher), Zahl der Listen bei „übrige". */
  unter: string | null;
  bekannt: boolean;
  uebrige: boolean;
  empfaenger: number;
  anfos: number;
  anfos_vorlaeufig: number;
  anfos_je_1000: number | null;
  wert_summe: number;
  mit_wert: number;
  wert_je_1000: number | null;
  wenig_daten: boolean;
  kinder?: NachfassGruppe[];
}

interface NachfassRoh {
  publisher: string;
  label: string;
  bekannt: boolean;
  empfaenger: number;
  anfos: number;
  anfos_vorlaeufig: number;
  anfos_je_1000: number | null;
  wert_summe: number;
  mit_wert: number;
  wert_je_1000: number | null;
}

const je1000 = (z: number, n: number): number | null => (n > 0 ? (z * 1000) / n : null);

/**
 * Publisher unter `schwelle` Anfos (ohne vorläufige) → eine Zeile „übrige Listen" mit den Einzelnen als Kindern (Persona-
 * Umbau A1, Regel 4). Erst ab zwei kleinen Publishern; einer allein bleibt eine eigene (graue) Zeile. Die Summen der
 * Gruppe rechnen wie das Backend (`nachfassZahlen`): € je 1.000 null, wenn keine Anfo einen Wert hat.
 */
export function nachfassGruppen(zeilen: NachfassRoh[], schwelle: number): NachfassGruppe[] {
  const fest = (r: { anfos: number; anfos_vorlaeufig: number }) => r.anfos - r.anfos_vorlaeufig;
  const alsZeile = (r: NachfassRoh): NachfassGruppe => ({
    key: r.publisher,
    label: r.label,
    unter: r.bekannt && r.label !== r.publisher ? r.publisher : null,
    bekannt: r.bekannt,
    uebrige: false,
    empfaenger: r.empfaenger,
    anfos: r.anfos,
    anfos_vorlaeufig: r.anfos_vorlaeufig,
    anfos_je_1000: r.anfos_je_1000,
    wert_summe: r.wert_summe,
    mit_wert: r.mit_wert,
    wert_je_1000: r.wert_je_1000,
    wenig_daten: fest(r) < schwelle,
  });
  const gross = zeilen.filter((r) => fest(r) >= schwelle).map(alsZeile);
  const klein = zeilen.filter((r) => fest(r) < schwelle).map(alsZeile);
  if (klein.length < 2) return [...gross, ...klein];
  const s = (k: 'empfaenger' | 'anfos' | 'anfos_vorlaeufig' | 'wert_summe' | 'mit_wert') => klein.reduce((a, r) => a + r[k], 0);
  const empf = s('empfaenger');
  const anfos = s('anfos');
  const vorl = s('anfos_vorlaeufig');
  const wert = s('wert_summe');
  const mitWert = s('mit_wert');
  const uebrige: NachfassGruppe = {
    key: '__uebrige',
    label: 'übrige Listen',
    unter: `${klein.length} Listen mit je unter ${schwelle} Anfos`,
    bekannt: false,
    uebrige: true,
    empfaenger: empf,
    anfos,
    anfos_vorlaeufig: vorl,
    anfos_je_1000: je1000(anfos - vorl, empf),
    wert_summe: wert,
    mit_wert: mitWert,
    wert_je_1000: empf <= 0 ? null : anfos === 0 ? 0 : mitWert > 0 ? je1000(wert, empf) : null,
    wenig_daten: anfos - vorl < schwelle,
    kinder: [...klein].sort((a, b) => b.empfaenger - a.empfaenger || a.key.localeCompare(b.key)),
  };
  return [...gross, uebrige];
}

/** Listen, deren Abdeckung in Webinaren mit Nachfass unter `grenze` % liegt (ab `minAnmelder` Anmeldern). */
export function abdeckungWarnung<T extends { label: string; anmelder_mit_nachfass: number; abdeckung_mit_nachfass: number | null }>(
  zeilen: T[],
  grenze = 90,
  minAnmelder = 50,
): T[] {
  return zeilen.filter((r) => r.anmelder_mit_nachfass >= minAnmelder && r.abdeckung_mit_nachfass !== null && r.abdeckung_mit_nachfass < grenze);
}

// ─── Cross-Sell ─────────────────────────────────────────────────────────────────────────────

export interface CrossSellBefundZeile {
  kunden: number;
  zahler: number;
}

export function crossSellBefund(stufen: CrossSellBefundZeile[], minKunden = 50): Befund {
  if (stufen.length < 2) return { fehlt: 'Kein Befund: weniger als zwei Stufen.' };
  const duenn = stufen.filter((s) => s.kunden < minKunden);
  if (duenn.length > 0) return { fehlt: `Kein Befund: eine Stufe hat weniger als ${minKunden} Kunden.` };
  const quoten = stufen.map((s) => (s.zahler * 100) / s.kunden);
  const kette = quoten.map((q) => formatNumber(q, 0)).join(' → ');
  const steigt = quoten.every((q, i) => i === 0 || q > quoten[i - 1]);
  return {
    text: steigt
      ? `Mit jeder weiteren Anfo steigt der Anteil zahlender Kunden (${kette} %) — Mehrfachkunden sind aber auch länger dabei (letzte Spalte); ob eine zweite Anfo zahlende Kunden macht, zeigt das allein nicht. Ansatz für einen Test: Zusatzangebote zuerst an Kunden, die schon bezahlt haben.`
      : `Anteil zahlender Kunden je Stufe: ${kette} % — kein durchgehender Anstieg mit der Zahl der Anfos.`,
  };
}
